import { Observable, Subject, of, Operator, OperatorFunction } from 'rxjs';
import { KeepAliveService } from './keep-alive.service';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs/operators';


interface CacheContent {
	expiry: number;
	value: any;
}

/**
 * Cache Service is an observables based in-memory cache implementation
 * Keeps track of in-flight observables and sets a default expiry for cached values
 * @export
 * @class CacheService
 */
@Injectable()
export class CacheService {
	private debug = false;
	private debugColor = "background-color:white;color:#060505;font-weight:900;";
	private cache: Map<string, CacheContent> = new Map<string, CacheContent>();
	private inFlightObservables: Map<string, Subject<any>> = new Map<string, Subject<any>>();
	readonly DEFAULT_MAX_AGE: number = 300000; // 300000 is five minutes;

	constructor(private keepAlive: KeepAliveService, private http: HttpClient) {
	}
	/**
	 * Gets the value from cache if the key is provided.
	 * If no value exists in cache, then check if the same call exists
	 * in flight, if so return the subject. If not create a new
	 * Subject inFlightObservable and return the source observable.
	 * @param key the name of the key in the cache
	 * @param fallback the fallback call to make if theres no cache
	 * @param maxAge how long in ms to keep the cache before expiration
	 * @param forceRefresh true will call fallback and empty the cache for this key
	 */
	get(key: string, fallback?: Observable<any>, maxAge?: number, forceRefresh: Boolean = false): Observable<any> | Subject<any> {
		//console.log("Checking cache/flight");

		if (forceRefresh) {
			if (this.debug) { console.log(`%cCACHE SVC: Removing Key From Cache: ${key}`, this.debugColor, this.cache); }
			this.cache.delete(key);
			if (this.debug) { console.log(`%cCACHE SVC: Removed Key From Cache:: ${key}`, this.debugColor, this.cache); }
		}
		if (this.hasValidCachedValue(key)) {
			if (this.debug) { console.log(`%cCACHE SVC: Getting from cache ${key}`, this.debugColor, this.cache.get(key).value); }
			this.keepAlive.touch();
			return of(this.cache.get(key).value); // .delay(500); // simulate fetch with a delay.
		}

		if (!maxAge) {
			maxAge = this.DEFAULT_MAX_AGE;
		}

		if (this.inFlightObservables.has(key)) {
			if (this.debug) { console.log("%cCACHE SVC: Key is in flight.", this.debugColor, key); }
			return this.inFlightObservables.get(key);
		} else if (fallback && fallback instanceof Observable) {
			this.inFlightObservables.set(key, new Subject());
			if (this.debug) { console.log(`%cCACHE SVC: Not in flight or cached, calling api for ${key}`, this.debugColor); }
			return (fallback).pipe(
				tap((value) => { this.set(key, value, maxAge); })
			);
		} else {
			if (this.debug) { console.log("%cCACHE SVC: Key does not exist in cache or in flight.", this.debugColor, key); }
			return Observable.throw('Requested key is not available in Cache');
		}

	}

	/**
	 * Sets the value with key in the cache
	 * Notifies all observers of the new value
	 */
	set(key: string, value: any, maxAge: number = this.DEFAULT_MAX_AGE): void {
		this.cache.set(key, { value: value, expiry: Date.now() + maxAge });
		this.notifyInFlightObservers(key, value);
	}

	/**
	 * Checks if the a key exists in cache
	 */
	has(key: string): boolean {
		return this.cache.has(key);
	}

	/**
	 * Publishes the value to all observers of the given
	 * in progress observables if observers exist.
	 */
	private notifyInFlightObservers(key: string, value: any): void {
		if (this.inFlightObservables.has(key)) {
			const inFlight = this.inFlightObservables.get(key);
			const observersCount = inFlight.observers.length;
			if (observersCount) {
				//console.log(`%cNotifying ${inFlight.observers.length} flight subscribers for ${key}`, 'color: blue');
				if (this.debug) { console.log(`%cCACHE SVC: Notifying ${inFlight.observers.length} flight subscribers for ${key}`, this.debugColor); }
				inFlight.next(value);
			}
			inFlight.complete();
			this.inFlightObservables.delete(key);
		}
	}

	/**
	 * Checks if the key exists and   has not expired.
	 */
	private hasValidCachedValue(key: string): boolean {
		if (this.cache.has(key)) {
			if (this.cache.get(key).expiry < Date.now()) {
				this.cache.delete(key);
				return false;
			}
			return true;
		} else {
			return false;
		}
	}
	/**
	 * Invalidates a cached call
	 * @param key The cache key to invalidate
	 */
	public invalidateCacheObj(key: string) {
		this.cache.delete(key);
	}
	/**
	 * Removes the key from the cache and re-makes the call
	 * @param key Key to refresh
	 */
	public refreshCache_GET(key: string, maxAge?: number) {
		console.log("PRE-DELETED CACHE: ", this.cache);
		if (this.debug) { console.log("%cCACHE SVC: Logging cache object before refresh.", this.debugColor, this.cache); }
		this.cache.delete(key);
		if (this.debug) { console.log("%cCACHE SVC: Logging cache object after refresh.", this.debugColor, this.cache); }
		this.get(key, this.http.get(key), maxAge).toPromise().then(resp => {
			//ey we done
		});
	}

	stringify(obj: object): string {
		// const o = Object(obj)
		// 	.reduce((acc, [k, v]) => (v==null ? acc : {...acc, [k]: v}), {});
		const o = Object.keys(obj)
			.filter(function (k) { return obj[k] != null; })
			.reduce(function (acc, k) { acc[k] = obj[k]; return acc; }, {});
		return JSON.stringify(o);
	}
}
